import { useState } from 'react';

import {
  useGetActiveDPHQuery,
  useGetNotificationFrequenciesQuery,
  useUpdateTenantDPHMutation,
} from '@features/tenants';
import { Alert, Box, Typography } from '@mui/material';

import { InlineLoader } from '@/components/common';
import { logger } from '@/utils/logger';

import { DPHEditModal } from './DPHEditModal';
import { HistoryModal } from './HistoryModal';
import { LicensedPeriodCard } from './LicensedPeriodCard';
import { MonthlyCard } from './MonthlyCard';

interface DPHSettingsProps {
  tenantId: string;
}

/**
 * Main DPH settings component
 * Fetches DPH data and notification frequencies
 * Manages modals and card state
 * Admin-only feature
 */
export const DPHSettings = ({ tenantId }: DPHSettingsProps) => {
  const [type, setType] = useState<string>('LicensedPeriod');
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [editModal, setEditModal] = useState(false);

  // Fetch DPH data
  const { data: dphData, isLoading: dphLoading, error: dphError } = useGetActiveDPHQuery(tenantId);

  // Fetch notification frequencies
  const { data: notificationFrequencies = [], isLoading: freqLoading } =
    useGetNotificationFrequenciesQuery();

  // Update DPH mutation
  const [updateTenantDPH] = useUpdateTenantDPHMutation();

  const handleSave = async (updateData: {
    notificationEmails: string[];
    notificationFrequencyId: number;
    threshold: number;
  }) => {
    try {
      await updateTenantDPH({
        tenantId,
        dphType: type === 'LicensedPeriod' ? 'licensedPeriod' : 'monthly',
        data: updateData,
      }).unwrap();
    } catch (error) {
      logger.error('Failed to update DPH:', error);
      alert('Request Failed. Please enter valid data');
    }
  };

  // Loading state
  if (dphLoading || freqLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4, gap: 2 }}>
        <InlineLoader size={24} />
        <Typography>Loading Data Processing Hours...</Typography>
      </Box>
    );
  }

  // Error state
  if (dphError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Failed to load Data Processing Hours data.</Alert>
      </Box>
    );
  }

  // Check if DPH is subscribed (empty data means not subscribed)
  if (!dphData || Object.keys(dphData).length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          Data Processing Hours is not subscribed for this tenant. Please contact support to enable
          this feature.
        </Alert>
      </Box>
    );
  }

  const { monthlyDPH, licensedPeriodDPH, tenantDPHAdditionalPurchases } = dphData;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Data Processing Hours
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Monitor and manage your data processing hour consumption
      </Typography>

      {/* Two Cards Side by Side */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 3,
        }}
      >
        {/* Licensed Period Card */}
        <LicensedPeriodCard
          data={{ licensedPeriodDPH, tenantDPHAdditionalPurchases }}
          setType={setType}
          setEditModal={setEditModal}
          notificationFrequency={notificationFrequencies}
        />

        {/* Monthly Card */}
        {monthlyDPH && monthlyDPH.months && monthlyDPH.months.length > 0 && (
          <MonthlyCard
            data={monthlyDPH}
            setType={setType}
            setShowHistoryModal={setShowHistoryModal}
            setEditModal={setEditModal}
            notificationFrequency={notificationFrequencies}
          />
        )}
      </Box>

      {/* Modals */}
      <HistoryModal
        isOpen={showHistoryModal}
        setIsOpen={setShowHistoryModal}
        data={{ licensedPeriodDPH, monthlyDPH }}
      />

      <DPHEditModal
        data={dphData}
        type={type}
        isOpen={editModal}
        setIsOpen={setEditModal}
        notificationFrequency={notificationFrequencies}
        onSave={handleSave}
      />
    </Box>
  );
};
